import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNotification } from "../../context/NotificationContext";
import api from "../../api/client";
import { User } from "../../types";

const UpdateProfile: React.FC = () => {
  const { user, setUser } = useAuth();
  const { notify } = useNotification();

  const [formData, setFormData] = useState({
    username: "",
    first_name: "",
    last_name: "",
    phone_number: "",
    email: "", // ✅ added email
  });

  const [loading, setLoading] = useState(false);

  // ✅ Fetch user profile on mount
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const { data } = await api.get<User>("/auth/profile/");
        setUser(data);
        setFormData({
          username: data.username || "",
          first_name: data.first_name || "",
          last_name: data.last_name || "",
          phone_number: data.phone_number || "",
          email: data.email || "", // ✅ populate email
        });
      } catch (err) {
        console.error("Failed to fetch user profile:", err);
      }
    };
    fetchUser();
  }, [setUser]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.put<User>("/auth/profile/", formData);
      notify("success", "Profile updated successfully!");
      setUser(data);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        "Failed to update profile. Please try again.";
      notify("error", errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-4xl font-extrabold text-gray-900 mb-8 text-center">
        Update Your Profile
      </h1>
      <div className="max-w-xl mx-auto bg-white p-8 rounded-lg shadow-xl border border-gray-200">
        <form onSubmit={handleSubmit} className="space-y-6">
          {["username", "first_name", "last_name", "phone_number", "email"].map(
            (field) => (
              <div key={field}>
                <label
                  htmlFor={field}
                  className="block text-sm font-semibold text-gray-700 mb-1 capitalize"
                >
                  {field.replace("_", " ")}
                </label>
                <input
                  type={field === "email" ? "email" : "text"} // ✅ type email
                  id={field}
                  name={field}
                  value={(formData as any)[field]}
                  onChange={handleChange}
                  className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition duration-150 ease-in-out"
                  required={["username", "email"].includes(field)} // ✅ email is required
                />
              </div>
            )
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-lg font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            {loading ? "Updating..." : "Save Changes"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default UpdateProfile;
